import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "store";
import { useBillingPortal } from "hooks/useBillingPortal";
import { IconWarning } from "icons";
import Button from "components/Button";
import { declineMessageKey, payInvoiceUrl } from "utils/paymentProblem";

/**
 * Says on every page that the plan's payment failed (P7 Faz 1, Kardem).
 *
 * A failed renewal leaves the team on `past_due` with no paid access and no credits (billing lifecycle R3, no grace) —
 * and until now only the Account page said so. `user.hasPaymentProblem` / `canManageBilling` come from `GET /users/{id}`
 * (`mapUserDetailToRedux`), which every routed page refreshes on mount.
 *
 * - Billing manager: "Update payment method" opens the Billing Portal, with a session fetched at click time
 *   (`useBillingPortal`, same tab). Nothing is requested on render.
 * - Member: who can fix it — they cannot open the team's portal.
 * - P7 F2-F1: when v3 sends `payment_problem` (detail only — the gate stays `hasPaymentProblem`), the decline reason for
 *   everyone and, for a billing manager, "Pay invoice" on Stripe's hosted invoice page (same tab).
 *
 * Not dismissible: a hidden warning is how the incident went unnoticed. In the page flow (not sticky), so it never covers
 * the top bars or a page's own controls. A suspended team gets its own screen instead.
 */
const BillingRecoveryBanner = () => {
  const { t } = useTranslation();
  const userId = useAppSelector((state) => state.common.user.id);
  const hasPaymentProblem = useAppSelector((state) => state.common.user.hasPaymentProblem === true);
  const canManageBilling = useAppSelector((state) => state.common.user.canManageBilling === true);
  const teamSuspended = useAppSelector((state) => state.common.teamSuspended === true);
  const paymentProblem = useAppSelector((state) => state.common.user.paymentProblem);
  const reasonKey = declineMessageKey(paymentProblem);
  const invoiceUrl = payInvoiceUrl(paymentProblem, canManageBilling);
  // Inline, not a snackbar: on NotAuthorized there is no snackbar renderer, and the failure belongs next to the button.
  const [error, setError] = useState<string | null>(null);
  const { openPortal, isOpening } = useBillingPortal({ onError: setError });

  if (!userId || !hasPaymentProblem || teamSuspended) return null;

  return (
    <div role="alert" data-testid="billing-recovery-banner" className="flex w-full flex-wrap items-center justify-center gap-x-4 gap-y-2 border-b border-orange/40 bg-orange/10 px-4 py-2.5">
      <div className="flex min-w-0 items-center gap-2">
        <IconWarning className="h-5 w-5 flex-shrink-0 text-orange" />
        <div className="flex min-w-0 flex-col">
          {canManageBilling ? (
            <span className="text-bodySm text-trueBlack dark:text-white">{t("account:plan_billing.payment_problem")}</span>
          ) : (
            <span className="text-bodySm text-trueBlack dark:text-white" data-testid="billing-recovery-ask-admin">
              {t("account:plan_billing.payment_problem_member")}
            </span>
          )}
          {reasonKey && (
            <span className="text-bodySm font-medium text-trueBlack dark:text-white" data-testid="billing-recovery-reason">
              {t(reasonKey)}
            </span>
          )}
        </div>
      </div>
      {canManageBilling && (
        <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
          {invoiceUrl && (
            <a href={invoiceUrl} className="btn btn-secondary-small" data-testid="billing-recovery-pay-invoice">
              {t("billing:past_due_modal.pay_invoice")}
            </a>
          )}
          <Button
            type="button"
            className="btn-primary-small"
            disabled={isOpening}
            onClick={() => {
              setError(null);
              openPortal();
            }}
            data-testid="billing-recovery-update-payment"
          >
            {isOpening ? t("account:plan_billing.manage_billing_loading") : t("account:plan_billing.update_payment_method")}
          </Button>
        </div>
      )}
      {error && (
        <span className="w-full text-center text-bodySm text-orange" data-testid="billing-recovery-error">
          {error}
        </span>
      )}
    </div>
  );
};

export default BillingRecoveryBanner;
